/**
 * BlocoTelegram — "Avisos pelo Telegram" em Configurações → Vigias e alertas
 * CI Sistemas de Informática Ltda. — AcessoiD, Onda 117
 *
 * Os avisos vão para CADA PESSOA escolhida — decisão do Iatagã em 28/09/2026, quando
 * a tela "Adicionar membros" do grupo do Telegram não mostrou o bot. Cada pessoa abre
 * o bot e toca em "Iniciar"; o servidor grava quem se inscreveu (o Telegram só guarda
 * 24 h) e aqui o administrador adiciona ou remove com um clique, sem decorar id.
 *
 * O token do bot não passa por aqui: mora no .env do servidor, e a rota só diz se
 * ele está configurado.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import '../styles/BlocoTelegram.css';

const TIPO = { falha: 'Falha', lembrete: 'Lembrete', volta: 'Voltou', teste: 'Teste' };

const semHtml = (t) => String(t || '').replace(/<[^>]+>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
const horaDe = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
export const destinosDe = (texto) => [...new Set(String(texto || '').split(/[\s,;]+/).map(x => x.trim()).filter(x => /^-?\d{1,20}$/.test(x)))];

export default function BlocoTelegram({ settings = {}, handleSave }) {
  const [estado, setEstado] = useState(null);
  const [falhou, setFalhou] = useState(false);
  const [inscritos, setInscritos] = useState(null);
  const [procurando, setProcurando] = useState(false);
  const [testando, setTestando] = useState(false);

  const destinos = destinosDe(settings.alert_telegram_chat_id);
  const gravar = (lista) => handleSave && handleSave('alert_telegram_chat_id', lista.join(', '));

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/api/alerts/telegram');
      setEstado(r.data);
      setFalhou(false);
    } catch (_) {
      setFalhou(true);
    }
  }, []);

  const procurar = useCallback(async (silencioso = false) => {
    setProcurando(true);
    try {
      const r = await api.get('/api/alerts/telegram/chats');
      setInscritos(r.data.chats || []);
      if (r.data.aviso && !silencioso) toast.error(r.data.aviso);
    } catch (e) {
      if (!silencioso) toast.error(`Não foi possível ler os inscritos: ${e.response?.data?.error || e.message}`);
    } finally {
      setProcurando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  // Com gente já escolhida, carrega os nomes sozinho: um id solto não diz quem é.
  useEffect(() => { if (estado?.token_configurado && destinos.length && inscritos === null) procurar(true); },
    [estado, destinos.length, inscritos, procurar]);

  const testar = async () => {
    setTestando(true);
    try {
      const r = await api.post('/api/alerts/telegram/test', { chat_id: destinos.join(', ') });
      const recusados = (r.data.resultados || []).filter(x => !x.ok);
      if (recusados.length) toast.error(`Enviada, mas ${recusados.length} não recebeu: ${recusados.map(x => x.error).join('; ')}`, { duration: 8000 });
      else toast.success('Mensagem de teste enviada. Confira no Telegram.');
    } catch (e) {
      toast.error(`O Telegram recusou: ${e.response?.data?.error || e.message}`, { duration: 8000 });
    } finally {
      setTestando(false);
      carregar();
    }
  };

  if (falhou) return <div className="tga-bloco tga-aviso" role="alert">Não foi possível ler o estado dos avisos. Recarregue a página.</div>;
  if (!estado) return <div className="tga-bloco tga-carregando">Consultando o bot…</div>;

  if (!estado.token_configurado) {
    return (
      <div className="tga-bloco tga-aviso" role="status">
        O bot do Telegram não está configurado neste servidor (falta o token no arquivo de ambiente do servidor).
        Os avisos não podem ser enviados até isso ser feito pelo suporte.
      </div>
    );
  }

  const bot = estado.bot;
  const link = bot ? `https://t.me/${bot.username}` : null;
  const porId = new Map((inscritos || []).map(c => [String(c.id), c]));
  const nomeDe = (id) => {
    const c = porId.get(id);
    return c ? `${c.name}${c.username ? ` (@${c.username})` : ''}` : `id ${id}`;
  };
  const disponiveis = (inscritos || []).filter(c => !destinos.includes(String(c.id)));
  const abertos = (estado.incidentes || []).filter(i => !i.resolvido);

  return (
    <div className="tga-bloco">
      <div className="tga-linha">
        <span className="tga-rotulo">Bot</span>
        {bot
          ? <span><b>{bot.name}</b> (@{bot.username})</span>
          : <span className="tga-erro">sem resposta do Telegram{estado.bot_erro ? `: ${estado.bot_erro}` : ''}</span>}
      </div>

      {link && (
        <p className="tga-dica-neutra">
          Para receber os avisos, cada pessoa abre <a href={link} target="_blank" rel="noreferrer">{link}</a> no
          Telegram e toca em <b>Iniciar</b>. Depois clique em "Procurar inscritos" e adicione quem deve receber.
        </p>
      )}

      <div className="tga-linha tga-linha-topo">
        <span className="tga-rotulo">Quem recebe</span>
        {destinos.length === 0 ? (
          <span>ninguém escolhido</span>
        ) : (
          <ul className="tga-destinos">
            {destinos.map(id => (
              <li key={id} className={porId.get(id)?.blocked ? 'tga-recusado' : ''}>
                <span>{nomeDe(id)}{porId.get(id)?.blocked ? ' — bloqueou o bot' : ''}</span>
                <button type="button" className="tga-botao" onClick={() => gravar(destinos.filter(x => x !== id))}>Remover</button>
              </li>
            ))}
          </ul>
        )}
        <div className="tga-acoes">
          <button type="button" className="tga-botao" onClick={() => procurar(false)} disabled={procurando}>
            {procurando ? 'Procurando…' : 'Procurar inscritos'}
          </button>
          <button type="button" className="tga-botao tga-botao-principal" onClick={testar} disabled={testando || destinos.length === 0}>
            {testando ? 'Enviando…' : 'Enviar mensagem de teste'}
          </button>
        </div>
      </div>

      {inscritos && (
        disponiveis.length === 0 ? (
          <p className="tga-dica">
            {inscritos.length === 0
              ? 'Ninguém se inscreveu no bot ainda. Mande o link acima para a equipe.'
              : 'Todos os inscritos já recebem os avisos.'}
          </p>
        ) : (
          <ul className="tga-grupos">
            {disponiveis.map(c => (
              <li key={c.id} className="tga-grupo">
                <span>
                  <b>{c.name}</b>{c.username ? ` (@${c.username})` : ''}{' '}
                  <small>{c.type === 'private' ? 'pessoa' : 'grupo'} · inscrito em {horaDe(c.first_seen)}{c.blocked ? ' · bloqueou o bot' : ''}</small>
                </span>
                {!c.blocked && <button type="button" className="tga-botao" onClick={() => gravar([...destinos, String(c.id)])}>Adicionar</button>}
              </li>
            ))}
          </ul>
        )
      )}

      {abertos.length > 0 && (
        <div className="tga-abertos">
          <span className="tga-rotulo">Em falha agora</span>
          <ul>
            {abertos.map(i => (
              <li key={i.key}>
                {i.nome || i.key} — desde {horaDe(i.desde)} {i.avisado ? '(avisado)' : '(ainda na espera)'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(estado.ultimos || []).length > 0 && (
        <div className="tga-historico">
          <span className="tga-rotulo">Últimos envios</span>
          <ul>
            {estado.ultimos.slice(0, 8).map(n => (
              <li key={n.id} className={n.ok ? '' : 'tga-recusado'}>
                <span className="tga-quando">{horaDe(n.created_at)}</span>
                <span className="tga-tipo">{TIPO[n.kind] || n.kind}</span>
                <span className="tga-texto">{n.ok ? semHtml(n.message).split('\n')[0] : `recusado: ${n.error}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
